/**
 * Intro/Outro Static Preview Component
 * 
 * Shows a small preview box that expands on hover to show a larger preview.
 * Used in settings panels to verify configurations without interfering with video playback.
 */

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { IntroClipConfig, OutroClipConfig } from '../../types/overlayConfig';

interface IntroOutroStaticPreviewProps {
  introConfig?: IntroClipConfig;
  outroConfig?: OutroClipConfig;
  type: 'intro' | 'outro';
  aspectRatio?: 'original' | '16:9' | '9:16' | '1:1' | '4:3';
}

const IntroOutroStaticPreview: React.FC<IntroOutroStaticPreviewProps> = ({
  introConfig,
  outroConfig,
  type,
  aspectRatio = 'original',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedPosition, setExpandedPosition] = useState({ left: 0, top: 0 });
  const previewRef = useRef<HTMLDivElement>(null);
  const config = type === 'intro' ? introConfig : outroConfig;

  // Calculate preview dimensions based on aspect ratio
  const previewDimensions = useMemo(() => {
    const baseWidth = 200; // Small preview width
    const baseHeight = 120; // Small preview height
    
    if (aspectRatio === 'original') {
      return { width: baseWidth, height: baseHeight };
    }
    
    const [w, h] = aspectRatio.split(':').map(Number);
    const ratio = w / h;
    
    // Maintain base width, adjust height
    return {
      width: baseWidth,
      height: baseWidth / ratio,
    };
  }, [aspectRatio]);

  // Calculate expanded preview dimensions based on aspect ratio
  const expandedDimensions = useMemo(() => {
    const baseWidth = 450;
    const baseHeight = 320;
    
    if (aspectRatio === 'original') {
      return { width: baseWidth, height: baseHeight };
    }
    
    const [w, h] = aspectRatio.split(':').map(Number);
    const ratio = w / h;
    
    // Maintain base width, adjust height
    return {
      width: baseWidth,
      height: baseWidth / ratio,
    };
  }, [aspectRatio]);

  // Generate gradient style - MUST be called before any early returns (Rules of Hooks)
  const gradientStyle = useMemo(() => {
    if (!config?.gradient?.enabled) {
      return { backgroundColor: config?.backgroundColor || '#000000' };
    }
    
    const gradient = config.gradient;
    if (gradient.type === 'radial') {
      const stops = gradient.stops || [50, 50];
      return {
        background: `radial-gradient(circle at ${stops[0]}% ${stops[1]}%, ${gradient.startColor}, ${gradient.endColor})`,
      };
    } else {
      const angle = gradient.direction || 0;
      return {
        background: `linear-gradient(${angle}deg, ${gradient.startColor}, ${gradient.endColor})`,
      };
    }
  }, [config?.gradient, config?.backgroundColor]);

  // Memoize preview content to ensure it updates when config changes
  // Scale content size based on aspect ratio (larger for vertical formats)
  const previewContent = useMemo(() => {
    // Scale factor for content - larger for vertical (9:16) to make content more visible
    const isVertical = aspectRatio === '9:16';
    const scaleFactor = isVertical ? 1.4 : 1.0; // 40% larger for 9:16
    
    if (type === 'intro') {
      return (
        <div className="text-center space-y-2 px-4 py-3" style={{ transform: `scale(${scaleFactor})` }}>
          {introConfig?.preset === 'preset1' ? (
            // Simple preset
            <div className="text-sm font-bold">Presented by Kridah Labs</div>
          ) : (
            // Detailed preset
            <>
              <div className="text-base font-bold">Presented by Kridah Labs</div>
              {introConfig?.showTournamentLogo && (
                <div className="text-xs opacity-90 mt-1">Tournament Name</div>
              )}
              {introConfig?.showTeamLogos && (
                <div className="flex items-center justify-center space-x-6 mt-3">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full mb-1 mx-auto"></div>
                    <div className="text-xs font-medium">Team A</div>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-full mb-1 mx-auto"></div>
                    <div className="text-xs font-medium">Team B</div>
                  </div>
                </div>
              )}
              {introConfig?.showLineups && (
                <div className="mt-3 text-xs opacity-80">
                  Lineups will be displayed here
                </div>
              )}
            </>
          )}
        </div>
      );
    } else {
      return (
        <div className="text-center space-y-3 px-4 py-3" style={{ transform: `scale(${scaleFactor})` }}>
          {outroConfig?.showThanksMessage && (
            <div className="text-base font-semibold">
              {outroConfig.customMessage || 'Thanks for watching!'}
            </div>
          )}
          <div className="flex flex-col items-center space-y-2">
            {outroConfig?.showTournamentLogo && (
              <div className="flex flex-col items-center space-y-1">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">Logo</span>
                </div>
                <div className="text-sm opacity-90">Tournament Name</div>
              </div>
            )}
          </div>
        </div>
      );
    }
  }, [
    type,
    aspectRatio,
    introConfig?.preset,
    introConfig?.showTournamentLogo,
    introConfig?.showTeamLogos,
    introConfig?.showLineups,
    outroConfig?.showThanksMessage,
    outroConfig?.customMessage,
    outroConfig?.showTournamentLogo,
  ]);

  // Calculate position for expanded preview when clicked
  useEffect(() => {
    if (isExpanded && previewRef.current) {
      const rect = previewRef.current.getBoundingClientRect();
      setExpandedPosition({
        left: rect.right + 20, // 20px to the right of small preview
        top: rect.top + rect.height / 2, // Center vertically
      });
    }
  }, [isExpanded]);

  // Close expanded preview when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isExpanded && previewRef.current && !previewRef.current.contains(event.target as Node)) {
        // Check if click is outside the expanded preview too
        const expandedElement = document.querySelector('[data-expanded-preview]');
        if (expandedElement && !expandedElement.contains(event.target as Node)) {
          setIsExpanded(false);
        }
      }
    };

    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isExpanded]);

  // Don't show preview if not enabled - AFTER hooks
  if (!config?.enabled) {
    return null;
  }

  return (
    <div className="relative">
      {/* Small preview box */}
      <div
        ref={previewRef}
        className="relative rounded border border-gray-300 overflow-hidden cursor-pointer transition-all duration-200 hover:border-blue-500 hover:shadow-md"
        style={{
          width: `${previewDimensions.width}px`,
          height: `${previewDimensions.height}px`,
          ...gradientStyle,
          opacity: config.opacity ?? 1.0,
          color: config.textColor || '#FFFFFF',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setIsExpanded(!isExpanded);
        }}
      >
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-xs font-semibold opacity-80">
            {type === 'intro' ? 'Intro Preview' : 'Outro Preview'}
          </div>
        </div>
        {/* Click indicator */}
        <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded">
          Click to expand
        </div>
      </div>

      {/* Expanded preview on click - positioned to the right */}
      {isExpanded && (
        <div
          data-expanded-preview
          className="fixed z-[9999] rounded-lg border-2 border-blue-500 shadow-2xl overflow-hidden bg-white pointer-events-auto animate-fadeIn"
          style={{
            width: `${expandedDimensions.width}px`,
            height: `${expandedDimensions.height}px`,
            left: `${expandedPosition.left}px`,
            top: `${expandedPosition.top}px`,
            transform: 'translateY(-50%)',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Preview content with gradient background */}
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              ...gradientStyle,
              opacity: config.opacity ?? 1.0,
              color: config.textColor || '#FFFFFF',
            }}
          >
            {previewContent}
          </div>
        </div>
      )}
      
      {/* Add CSS animation - simple fade in */}
      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default IntroOutroStaticPreview;
